'use client'

import {
  FirstBillingPaymentRedirectedSearchParamsSchema,
  firstBillingPaymentRedirectedSearchParamsSchema,
  LumpSumPaymentRedirectedSearchParamsSchema,
  lumpSumPaymentRedirectedSearchParamsSchema,
  TrialPaymentRedirectedSearchParamsSchema,
  trialPaymentRedirectedSearchParamsSchema,
} from '@features/payment'
import { loggingPaymentSearchParamsAction } from '@features/payment/api'
import { isPaymentCanceled } from '@features/payment/lib/is-payment-canceled'
import { env, productionAppEnvSchema } from '@shared/config/env'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'

export const usePaymentRedirectedSearchParams = (): Nullable<
  | Brand<TrialPaymentRedirectedSearchParamsSchema, 'TRIAL'>
  | Brand<FirstBillingPaymentRedirectedSearchParamsSchema, 'FIRST_BILLING'>
  | Brand<LumpSumPaymentRedirectedSearchParamsSchema, 'LUMP_SUM'>
> => {
  const searchParams = useSearchParams()

  const searchParamsObject = Object.fromEntries(searchParams?.entries() ?? [])
  const isCanceled = isPaymentCanceled(searchParamsObject)

  const trialPaymentRedirectedSearchParams = useMemo(
    () => trialPaymentRedirectedSearchParamsSchema.safeParse(searchParamsObject),
    [searchParamsObject],
  )
  const firstBillingPaymentRedirectedSearchParams = useMemo(
    () => firstBillingPaymentRedirectedSearchParamsSchema.safeParse(searchParamsObject),
    [searchParamsObject],
  )
  const lumpSumPaymentRedirectedSearchParams = useMemo(
    () => lumpSumPaymentRedirectedSearchParamsSchema.safeParse(searchParamsObject),
    [searchParamsObject],
  )

  const validatedReturn = trialPaymentRedirectedSearchParams.success
    ? trialPaymentRedirectedSearchParams.data
    : firstBillingPaymentRedirectedSearchParams.success
      ? firstBillingPaymentRedirectedSearchParams.data
      : lumpSumPaymentRedirectedSearchParams.success
        ? lumpSumPaymentRedirectedSearchParams.data
        : null
  const returnType: 'TRIAL' | 'FIRST_BILLING' | 'LUMP_SUM' | 'INVALID' = trialPaymentRedirectedSearchParams.success
    ? 'TRIAL'
    : firstBillingPaymentRedirectedSearchParams.success
      ? 'FIRST_BILLING'
      : lumpSumPaymentRedirectedSearchParams.success
        ? 'LUMP_SUM'
        : 'INVALID'
  const orderId = /^(?:KR_P_|T?PODO_)[A-Za-z0-9_-]{1,121}$/.test(searchParamsObject.purchase_order_id ?? '')
    ? searchParamsObject.purchase_order_id
    : undefined
  const diagnostic = {
    orderId,
    returnType,
    provider: validatedReturn?.pg_provider,
    hasApprovalToken: Boolean(searchParamsObject.paymentToken || searchParamsObject.payment_token),
    hasBillingKey: Boolean(
      searchParamsObject.billingKey || searchParamsObject.customer_uid || searchParamsObject.issue_id,
    ),
    hasProviderError: Boolean(searchParamsObject.code || searchParamsObject.imp_success === 'false'),
    trialValid: trialPaymentRedirectedSearchParams.success,
    firstBillingValid: firstBillingPaymentRedirectedSearchParams.success,
    lumpSumValid: lumpSumPaymentRedirectedSearchParams.success,
  }
  useQuery({
    queryKey: ['loggingPaymentSearchParamsAction', diagnostic],
    queryFn: () => loggingPaymentSearchParamsAction(diagnostic),
    enabled:
      productionAppEnvSchema.safeParse(env.NEXT_PUBLIC_APP_ENV).success && Object.keys(searchParamsObject).length > 0,
    retry: false,
    staleTime: Infinity,
  })

  return useMemo(() => {
    // complete는 요청 전에 붙인 값이므로 취소 응답에서 완료 조회를 시작하지 않는다.
    if (isCanceled) {
      return null
    }

    if (trialPaymentRedirectedSearchParams.success) {
      return {
        __brand: 'TRIAL',
        ...trialPaymentRedirectedSearchParams.data,
      }
    }

    if (firstBillingPaymentRedirectedSearchParams.success) {
      return {
        __brand: 'FIRST_BILLING',
        ...firstBillingPaymentRedirectedSearchParams.data,
      }
    }

    if (lumpSumPaymentRedirectedSearchParams.success) {
      return {
        __brand: 'LUMP_SUM',
        ...lumpSumPaymentRedirectedSearchParams.data,
      }
    }

    return null
  }, [
    isCanceled,
    firstBillingPaymentRedirectedSearchParams.data,
    firstBillingPaymentRedirectedSearchParams.success,
    lumpSumPaymentRedirectedSearchParams.data,
    lumpSumPaymentRedirectedSearchParams.success,
    trialPaymentRedirectedSearchParams.data,
    trialPaymentRedirectedSearchParams.success,
  ])
}
